/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {createSelector} from 'reselect';
import {createDeepEqualSelector} from '@ion/applications/src/store/selectors/SelectorUtils';
import {getAccountIds} from '@ion/core/src/entities/Accounts';
import {Account, AccountLink} from '@ion/core/src/types/entities';
import {GlobalGroupReducerState} from '@/store/types';

// input selectors ...
const globalGroupChooser    = ({globalGroup: {groupChooser}}: GlobalGroupReducerState) => groupChooser;
const globalGroupTitle      = ({globalGroup: {globalGroup}}: GlobalGroupReducerState) => globalGroup?.title || '';
const selectIsViewing       = ({globalGroup: {groupChooser}}: GlobalGroupReducerState) => groupChooser.isViewing;
const selectIsGGCLoading    = ({globalGroup}: GlobalGroupReducerState) => globalGroup.loading;
const selectGlobalGroupState = ({globalGroup}: GlobalGroupReducerState) => globalGroup;
const selectGlobalGroup     = ({globalGroup: {globalGroup}}: GlobalGroupReducerState) => globalGroup;
const selectAccounts        = ({globalGroup}: GlobalGroupReducerState) => globalGroup.globalGroupHierarchy;
const selectAccountsMap     = ({globalGroup}: GlobalGroupReducerState) => globalGroup.accountMap;
const selectCurrentGroup    = ({globalGroup}: GlobalGroupReducerState) => globalGroup.currentAccount;
const selectCurrentWebMapId = ({globalGroup}: GlobalGroupReducerState) => globalGroup.webMapId;
const ancestorAccountId     = ({globalGroup}: GlobalGroupReducerState) => globalGroup.ancestorAccountId;

const selectAccountHierarchy       = ({globalGroup: {accountHierarchy}}: GlobalGroupReducerState) => accountHierarchy;
export const selectGlobalGroupId   = ({globalGroup}: GlobalGroupReducerState) => globalGroup.globalGroupId;

// output selectors ...
export const isViewingglobalGroupSelector = createSelector(selectIsViewing,
  (isViewing: boolean): boolean => isViewing
);

export const globalGroupStateSelector = createDeepEqualSelector(selectGlobalGroupState,
  (globalGroupState) => globalGroupState
);

export const globalGroupSelector = createDeepEqualSelector(selectGlobalGroup,
  (globalGroup: AccountLink): AccountLink => globalGroup
);

export const globalCurrentGroupSelector = createDeepEqualSelector(selectCurrentGroup,
  (currentAccount: Account): Account => currentAccount
);

export const accountsSelector = createDeepEqualSelector(selectAccounts,
  (globalGroupHierarchy: AccountLink[]): AccountLink[] => globalGroupHierarchy
);

export const globalGroupIdSelector = createSelector(selectGlobalGroupId,
  (globalGroupId: number): number => globalGroupId
);

export const globalGroupLoadingSelector = createSelector(selectIsGGCLoading,
  (loading: boolean): boolean => loading
);

export const currentWebMapIdSelector = createSelector(selectCurrentWebMapId,
  (webMapId: string): string => webMapId
);

export const globalGroupChooserSelector = createDeepEqualSelector(globalGroupChooser,
  (globalGroup) => globalGroup
);

export const globalGroupTitleSelector = createSelector(globalGroupTitle,
  (title: string) => title
);

export const globalGroupAncestorAccountIdsSelector = createSelector(ancestorAccountId,
  (accountLinks: AccountLink[]) => accountLinks
);

export const globalGroupDecendantAccountIdsSelector = createSelector(
  selectAccounts,
  (globalGroupHierarchy: AccountLink[]) => {
    const descendantAccountId = getAccountIds(null, globalGroupHierarchy);
    return descendantAccountId;
  }
);

export const globalGroupAccountHierarchy = createDeepEqualSelector(selectAccountHierarchy,
  (accountHierarchy: AccountLink[]) => accountHierarchy
);

export const globalGroupAccountsMapSelector = createDeepEqualSelector(selectAccountsMap,
  (accountsMap: {[key: number]: AccountLink}) => accountsMap
);
